import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PrintOptions from './PrintOptions';
import { getProductDetails, api, getProductsCatalog, getProfile, normalizePaginatedItems, catalogItemKey } from '../apiClient';
import Session from 'supertokens-auth-react/recipe/session';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const ConfigureMockupPage = () => {
  const location = useLocation();
  const { selectedProduct, selectedImage: initialSelectedImage } = location.state || {};
  const [userFullName, setUserFullName] = useState('');
  const [displayedProductImage, setDisplayedProductImage] = useState(selectedProduct ? selectedProduct.image : '');
  const [productDetails, setProductDetails] = useState(null);
  const [selectedPlacement, setSelectedPlacement] = useState(null);
  const [printArea, setPrintArea] = useState({ width: 0, height: 0 });
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [size, setSize] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [liveProductData, setLiveProductData] = useState(selectedProduct);
  const [productsCatalog, setProductsCatalog] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState(initialSelectedImage);
  // Multi-placement: [{ placement: 'front'|'back'|..., image: {...} }]
  const [placements, setPlacements] = useState([]);

  useEffect(() => {
    setSelectedImage(initialSelectedImage);
  }, [initialSelectedImage]);

  // Initialize placements when productDetails + selectedImage available
  useEffect(() => {
    if (!productDetails?.files || !selectedImage) return;
    const files = productDetails.files.filter(f => f.type && f.type !== 'mockup' && f.type !== 'preview');
    const firstType = files[0]?.type || 'front';
    setPlacements(prev => {
      if (prev.length === 0) {
        return [{ placement: firstType, image: selectedImage }];
      }
      return prev;
    });
  }, [productDetails?.files, selectedImage]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await getProductsCatalog();
        setProductsCatalog(normalizePaginatedItems(data));
      } catch (err) {
        console.error("Error fetching products catalog:", err);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchProductDetails = async () => {
      if (liveProductData?.id != null) {
        const printingProvider =
          liveProductData.catalog_provider ||
          (liveProductData.provider_mappings?.Printify ? 'Printify' : null) ||
          (liveProductData.provider_mappings?.Printful ? 'Printful' : null) ||
          'Printful';
        try {
          const details = await getProductDetails(liveProductData.id, {
            printing_provider: printingProvider,
          });
          setProductDetails(details);
          setDisplayedProductImage(details.image);
        } catch (error) {
          console.error("Error fetching product details:", error);
        }
      }
    };

    fetchProductDetails();
  }, [liveProductData?.id, liveProductData?.catalog_provider]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        if (!(await Session.doesSessionExist())) return;
        const profile = await getProfile();
        if (profile?.full_name?.trim()) {
          setUserFullName(profile.full_name.trim());
        }
      } catch (err) {
        // User may not be logged in
      }
    };
    fetchUserProfile();
  }, []);

  useEffect(() => {
    const fetchGalleryImages = async () => {
      try {
        const loggedIn = await Session.doesSessionExist();
        if (!loggedIn) {
          setGalleryImages([]);
          return;
        }
        const response = await api.get('/gallery');
        const images = response.data?.map?.((img) => ({ ...img, id: img.id })) || [];
        setGalleryImages(images);
        if (initialSelectedImage) {
          const idx = images.findIndex(
            (img) => img.id === initialSelectedImage.id || img.image_url === initialSelectedImage.image_url || img.url === initialSelectedImage.url
          );
          setSelectedImageIndex(idx >= 0 ? idx : 0);
        }
      } catch (err) {
        console.error("Error fetching gallery images:", err);
      }
    };

    fetchGalleryImages();
  }, [initialSelectedImage?.id, initialSelectedImage?.image_url, initialSelectedImage?.url]);

  useEffect(() => {
    if (productDetails && !selectedPlacement) {
      // Set the default placement when product details are loaded
      const defaultPlacement = productDetails.options.find(option => option.id === 'placement')?.values[0];
      if (defaultPlacement) {
        setSelectedPlacement(defaultPlacement.id);
      }
    }
  }, [productDetails, selectedPlacement]);

  useEffect(() => {
    if (productDetails && selectedPlacement) {
      const placementFile = productDetails.files.find(file => file.type === selectedPlacement);
      if (placementFile) {
        setPrintArea({ width: placementFile.width, height: placementFile.height });
      }
    }
  }, [productDetails, selectedPlacement]);

  // Reset placements when product changes so we re-init with new product's options
  useEffect(() => {
    setPlacements([]);
  }, [liveProductData?.id, liveProductData?.catalog_provider]);

  // Initialize placements only when product details match the currently selected product (fixes "stuck on first product" / always "Front Print")
  useEffect(() => {
    if (
      !productDetails?.files ||
      !selectedImage ||
      productDetails?.id !== liveProductData?.id ||
      productDetails?.catalog_provider !== liveProductData?.catalog_provider
    ) {
      return;
    }
    const validFiles = productDetails.files.filter(
      (f) => f.type && f.type !== 'mockup' && f.type !== 'preview'
    );
    const firstType = validFiles[0]?.type || 'front';
    setPlacements((prev) => {
      if (prev.length > 0) return prev;
      return [{ placement: firstType, image: selectedImage }];
    });
  }, [productDetails, selectedImage, liveProductData?.id, liveProductData?.catalog_provider]);

  // Keep first placement's image in sync when user changes image via carousel (fixes image not updating in placement card)
  useEffect(() => {
    if (!selectedImage) return;
    setPlacements((prev) => {
      if (prev.length === 0) return prev;
      const first = prev[0];
      const curUrl = first?.image?.image_url || first?.image?.url;
      const newUrl = selectedImage?.image_url || selectedImage?.url;
      if (curUrl && newUrl && curUrl === newUrl && (first?.image?.id === selectedImage?.id || !selectedImage?.id)) return prev;
      return [{ ...first, image: selectedImage }, ...prev.slice(1)];
    });
  }, [selectedImage, selectedImage?.id, selectedImage?.image_url, selectedImage?.url]);

  const isImageInGallery = (img) =>
    galleryImages.some(
      (g) => g.id === img?.id || g.image_url === img?.image_url || g.url === img?.url
    );
  const displayableImages = (() => {
    if (galleryImages.length === 0) return selectedImage ? [selectedImage] : [];
    if (selectedImage && !isImageInGallery(selectedImage)) {
      return [selectedImage, ...galleryImages];
    }
    return galleryImages;
  })();
  const currentDisplayImage = displayableImages[selectedImageIndex] || selectedImage;

  const handleImagePrev = () => {
    if (displayableImages.length <= 1) return;
    const newIdx = selectedImageIndex <= 0 ? displayableImages.length - 1 : selectedImageIndex - 1;
    setSelectedImageIndex(newIdx);
    setSelectedImage(displayableImages[newIdx]);
  };

  const handleImageNext = () => {
    if (displayableImages.length <= 1) return;
    const newIdx = selectedImageIndex >= displayableImages.length - 1 ? 0 : selectedImageIndex + 1;
    setSelectedImageIndex(newIdx);
    setSelectedImage(displayableImages[newIdx]);
  };

  const displayableProducts = productsCatalog.length > 0 ? productsCatalog : (liveProductData ? [liveProductData] : []);
  const productIndex = displayableProducts.findIndex(
    (p) => catalogItemKey(p) === catalogItemKey(liveProductData),
  );
  const handleProductPrev = () => {
    if (displayableProducts.length <= 1) return;
    const newIdx = productIndex <= 0 ? displayableProducts.length - 1 : productIndex - 1;
    setLiveProductData(displayableProducts[newIdx]);
  };
  const handleProductNext = () => {
    if (displayableProducts.length <= 1) return;
    const newIdx = productIndex < 0 || productIndex >= displayableProducts.length - 1 ? 0 : productIndex + 1;
    setLiveProductData(displayableProducts[newIdx]);
  };

  if (!liveProductData || !selectedImage) {
    return (
      <div className="container mx-auto p-4 text-center">
        <h1 className="text-3xl font-bold mb-6">Error: Missing Data</h1>
        <p>Please select a product and an image to configure a mockup.</p>
        {/* Optionally, add navigation back to product catalog or gallery */}
      </div>
    );
  }

  const flowDesignThumb =
    selectedImage?.mockup_image_url || selectedImage?.image_url || selectedImage?.url;
  const flowDesignLabel = selectedImage?.prompt || 'Your artwork';

  return (
      <div className="container mx-auto p-4 sm:p-6 bg-background rounded-lg shadow-lg tour-configure-mockup max-w-full overflow-x-hidden">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 sm:mb-8 text-center text-gray-800">Configure Your Mockup</h1>

        <div
          className="mb-6 max-w-5xl mx-auto rounded-xl border border-emerald-200 bg-emerald-50/90 px-4 py-4 flex flex-col lg:flex-row lg:items-center gap-4 shadow-sm"
          role="status"
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <img
              src={displayedProductImage || liveProductData?.image || ''}
              alt=""
              className="w-14 h-14 rounded-lg object-contain border border-white bg-white shadow-sm flex-shrink-0"
            />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-emerald-900 uppercase tracking-wide">Product selected</p>
              <p className="font-medium text-gray-900 truncate">{liveProductData?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {flowDesignThumb && (
              <img
                src={flowDesignThumb}
                alt=""
                className="w-14 h-14 rounded-lg object-cover border border-white bg-white shadow-sm flex-shrink-0"
              />
            )}
            <div className="min-w-0">
              <p className="text-xs font-semibold text-emerald-900 uppercase tracking-wide">Design</p>
              <p className="font-medium text-gray-900 truncate">{flowDesignLabel}</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 lg:max-w-xs">Use the controls below to position your artwork on the product.</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* Selected Product Display - Slider to browse products visually */}
          <div className="border border-gray-200 rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Selected Product</h2>
            {displayableProducts.length >= 1 ? (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleProductPrev}
                  className="flex-shrink-0 p-2 rounded-full bg-white border-2 border-gray-300 hover:bg-gray-100 hover:border-gray-400 shadow-sm transition-colors"
                  aria-label="Previous product"
                >
                  <ChevronLeft size={24} className="text-gray-700" />
                </button>
                <div className="flex-1 min-w-0 flex flex-col items-center">
                  <img
                    src={displayedProductImage || liveProductData?.image}
                    alt={liveProductData?.name}
                    className="w-full h-64 object-contain rounded-lg border border-gray-200 bg-white"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    {(productIndex >= 0 ? productIndex : 0) + 1} / {displayableProducts.length}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleProductNext}
                  className="flex-shrink-0 p-2 rounded-full bg-white border-2 border-gray-300 hover:bg-gray-100 hover:border-gray-400 shadow-sm transition-colors"
                  aria-label="Next product"
                >
                  <ChevronRight size={24} className="text-gray-700" />
                </button>
              </div>
            ) : (
              <img src={displayedProductImage} alt={liveProductData.name} className="w-full h-64 object-contain mb-4" />
            )}
            <h3 className="text-lg font-bold text-gray-800 mt-4">{liveProductData.name}</h3>
            <p className="text-gray-600 text-sm">Estimated Price: ${liveProductData.selling_price ? liveProductData.selling_price.toFixed(2) : 'N/A'}</p>
            <p className="text-xs text-gray-500 mt-2">Ships to: {liveProductData.coverage_locations && liveProductData.coverage_locations.length > 0 ? liveProductData.coverage_locations.join(', ') : 'Worldwide'}</p>
            <p className="text-xs text-gray-500 mt-1" title={(() => {
              const list = liveProductData.supported_print_placements?.length > 0
                ? liveProductData.supported_print_placements.map(p => p.title)
                : productDetails?.files?.length
                  ? productDetails.files.filter(f => f.type && f.type !== 'mockup' && f.type !== 'preview').map(f => f.title || f.type)
                  : ['Front'];
              return list.join(', ');
            })()}>
              Print areas: {(() => {
                const allowed = new Set(['front', 'back', 'sleeve', 'chest', 'label', 'default', 'large', 'left', 'right', 'inside']);
                const filterValid = (arr) => arr.filter(t => allowed.has(t.toLowerCase()) || [...allowed].some(a => (t || '').toLowerCase().includes(a)));
                let list = [];
                if (liveProductData.supported_print_placements?.length > 0) list = liveProductData.supported_print_placements.map(p => p.title);
                else if (productDetails?.files?.length) {
                  list = productDetails.files.filter(f => f.type && f.type !== 'mockup' && f.type !== 'preview').map(f => f.title || f.type);
                  list = filterValid(list);
                }
                if (list.length === 0) list = ['Front'];
                const maxShow = 3;
                const shown = list.slice(0, maxShow).join(', ');
                const rest = list.length - maxShow;
                return rest > 0 ? `${shown} +${rest} more` : shown;
              })()}
            </p>
            <p className="text-xs text-gray-500 mt-1">Fulfillment: {liveProductData.avg_fulfillment_time ? `${liveProductData.avg_fulfillment_time} business days` : '5-7 business days'}</p>
            {liveProductData.shipping_estimate && <p className="text-xs text-green-600 font-bold mt-1">Est. Delivery: {liveProductData.shipping_estimate}</p>}
          </div>

          {/* Selected Image Display - Slider to browse generated images */}
          <div className="border border-gray-200 rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Selected Image</h2>
            {displayableImages.length >= 1 ? (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleImagePrev}
                  className="flex-shrink-0 p-2 rounded-full bg-white border-2 border-gray-300 hover:bg-gray-100 hover:border-gray-400 shadow-sm transition-colors"
                  aria-label="Previous image"
                >
                  <ChevronLeft size={24} className="text-gray-700" />
                </button>
                <div className="flex-1 min-w-0 flex flex-col items-center">
                  <img
                    src={(currentDisplayImage || selectedImage)?.image_url || (currentDisplayImage || selectedImage)?.url}
                    alt={(currentDisplayImage || selectedImage)?.prompt || 'Design'}
                    className="w-full h-64 object-contain rounded-lg border border-gray-200 bg-white"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    {selectedImageIndex + 1} / {displayableImages.length}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleImageNext}
                  className="flex-shrink-0 p-2 rounded-full bg-white border-2 border-gray-300 hover:bg-gray-100 hover:border-gray-400 shadow-sm transition-colors"
                  aria-label="Next image"
                >
                  <ChevronRight size={24} className="text-gray-700" />
                </button>
              </div>
            ) : (
              <img
                src={selectedImage?.image_url || selectedImage?.url}
                alt={selectedImage?.prompt}
                className="w-full h-64 object-contain mb-4"
              />
            )}
            <p className="text-gray-600 mt-4">Prompt: {(currentDisplayImage || selectedImage)?.prompt}</p>
            <p className="text-gray-600">Style: {(currentDisplayImage || selectedImage)?.style || 'Default'}</p>
          </div>
        </div>

        {/* Print Options Component */}
        <div className="tour-print-options">
          <PrintOptions
            key={productDetails?.id != null ? `${productDetails.id}-${productDetails.catalog_provider || 'Printful'}` : 'pending-details'}
            image={selectedImage}
            userFullName={userFullName}
            selectedProduct={liveProductData}
            onProductImageSelected={setDisplayedProductImage}
            onProductChange={(product) => setLiveProductData((prev) => ({ ...prev, ...product }))}
            productDetails={productDetails}
            position={position}
            size={size}
            rotation={rotation}
            printArea={printArea}
            placements={placements}
            setPlacements={setPlacements}
            displayableImages={displayableImages}
            onMockupGenerated={(mockupDetails) => {
              setDisplayedProductImage(mockupDetails.mockup_image_url);
              // Update the product data to include the new shipping estimate and price
              setLiveProductData(prev => ({...prev, ...mockupDetails}));
            }}
          />
        </div>
      </div>
  );
};

export default ConfigureMockupPage;